import { getTPOutletById } from 'app/connection/Connection';
import { useConnection } from 'app/connection/hooks/useConnection';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { G } from '@mobily/ts-belt';
import { Button, CircularProgress, Grid } from '@mui/material';
import { Coax } from 'app/outlets/api';

import { useDeleteCoax } from 'app/outlets/hooks/useDeleteCoax';
import * as styles from './styles';

export const DeleteDropAntennaCoaxialLink: FC<{
  propertyId: number;
  outletId: string;
  fiber: Coax;
  onSuccess?: Lambda<void, void>;
  onCancel?: Lambda<void, void>;
}> = ({ propertyId, outletId, fiber, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { data, isLoading: isLoadingConnection } = useConnection(propertyId);
  const {
    mutate,
    isLoading: isLoadingMutateConnection,
    isError: isErrorMutateConnection,
    isSuccess: isSuccessMutateConnection,
  } = useDeleteCoax(propertyId);

  useEffect(() => {
    if (isErrorMutateConnection) {
      toast.error(t('error.generalMessage'));
    }
  }, [isErrorMutateConnection]);

  useEffect(() => {
    if (isSuccessMutateConnection) {
      toast.success(t('general.updatedSuccessfully'));

      if (G.isNotNullable(onSuccess)) {
        onSuccess();
      }
    }
  }, [isSuccessMutateConnection]);

  const deleteFiber = useCallback(() => {
    if (G.isNotNullable(data)) {
      const pickedOutlet = getTPOutletById(data.serialized2, outletId);

      if (G.isNotNullable(pickedOutlet)) {
        mutate({ fiberId: fiber.id });
      }
    }
  }, [data, fiber, outletId]);

  const isLoading = useMemo(() => {
    return isLoadingConnection || isLoadingMutateConnection;
  }, [isLoadingConnection, isLoadingMutateConnection]);

  return (
    <Grid container>
      {isLoading && (
        <Grid container sx={styles.loaderContainer}>
          <CircularProgress />
        </Grid>
      )}
      {!isLoading && (
        <Grid container>
          <Grid container sx={styles.deleteFibersButtonsContainer}>
            <Button
              variant="contained"
              onClick={() => G.isNotNullable(onCancel) && onCancel()}
              data-testid="delete-fiber-cancel"
            >
              {t('general.cancel')}
            </Button>
            <Button variant="outlined" onClick={deleteFiber} data-testid="delete-fiber-accept">
              {t('general.accept')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
